import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

export default function AdminStock() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Data State
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState({
    totalCurrentStock: 0,
    totalSold: 0,
    totalPending: 0,
    totalUploaded: 0,
    totalRevenue: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
  });
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
    limit: 20
  });

  // Filters
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      fetchStock();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, page, search]);

  const fetchStock = async () => {
    setLoading(true);
    try {
      const isSearching = !!search.trim();
      const res = await axiosInstance.get("/api/reports/admin/stock-summary", {
        params: { sortBy, page, limit: isSearching ? 500 : 20, search: isSearching ? search.trim() : undefined }
      });
      
      const data = res.data || {};
      setProducts(data.products || []);
      setSummary(data.summary || {});
      setPagination(data.pagination || { totalItems: 0, totalPages: 1, currentPage: 1, limit: 20 });
    } catch (err) {
      console.error("Fetch stock error:", err);
    } finally {
      setLoading(false);
    }
  };

  const money = (v) => `₹${Number(v || 0).toFixed(2)}`;
  const nn = (v) => Number(v || 0);
  const isSearching = !!search.trim();

  // 🔍 Calculate local filtered products
  const filteredProducts = products.filter(p => 
    !isSearching || String(p.name || "").toLowerCase().startsWith(search.trim().toLowerCase())
  );

  const getStatusBadge = (p) => {
    if (p.isOutOfStock || Number(p.currentStock) === 0) {
      return <span style={{ ...styles.badge, background: "#fee2e2", color: "#b91c1c" }}>Out of Stock</span>;
    }
    if (p.isLowStock) {
      return <span style={{ ...styles.badge, background: "#fef3c7", color: "#b45309" }}>Low Stock</span>;
    }
    return <span style={{ ...styles.badge, background: "#dcfce7", color: "#15803d" }}>In Stock</span>;
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.h1}>Stock Summary</h2>
          <div style={styles.sub}>Track product stock, sales, and revenue</div>
        </div>
        <button style={styles.btn} onClick={() => navigate("/admin/dashboard")}>
          ← Dashboard
        </button>
      </div>

      {/* SUMMARY WIDGETS */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryBox, ...styles.boxBlue }}>
          <div style={styles.summaryLabel}>Total Uploaded</div>
          <div style={styles.summaryValue}>{summary.totalUploaded || 0}</div>
        </div>
        <div style={{ ...styles.summaryBox, ...styles.boxGreen }}>
          <div style={styles.summaryLabel}>Total Current Stock</div>
          <div style={styles.summaryValue}>{summary.totalCurrentStock || 0}</div>
        </div>
        <div style={{ ...styles.summaryBox, ...styles.boxPurple }}>
          <div style={styles.summaryLabel}>Total Sold</div>
          <div style={styles.summaryValue}>{summary.totalSold || 0}</div>
        </div>
        <div style={{ ...styles.summaryBox, ...styles.boxOrange }}>
          <div style={styles.summaryLabel}>Total Pending Orders</div>
          <div style={styles.summaryValue}>{summary.totalPending || 0}</div>
        </div>
        <div style={{ ...styles.summaryBox, ...styles.boxTeal }}>
          <div style={styles.summaryLabel}>Total Revenue</div>
          <div style={styles.summaryValue}>{money(summary.totalRevenue)}</div>
        </div>
        <div style={{ ...styles.summaryBox, ...styles.boxRed }}>
          <div style={styles.summaryLabel}>Alerts ⚠️</div>
          <div style={{...styles.summaryValue, fontSize: 16, marginTop: 4}}>
            Low Stock: <b style={{color: '#b45309'}}>{summary.lowStockCount || 0}</b> <br/>
            Out of Stock: <b style={{color: '#b91c1c'}}>{summary.outOfStockCount || 0}</b>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        {/* SEARCH & FILTERS */}
        <div style={styles.searchRow}>
          <div style={styles.searchWrap}>
             <span style={styles.searchIcon}>🔍</span>
             <input 
               style={styles.searchInput}
               placeholder="Search product name, brand, or category..."
               value={search}
               onChange={(e) => { setSearch(e.target.value); setPage(1); }}
             />
             {search && (
               <button onClick={() => setSearch("")} style={styles.searchClear}>✕</button>
             )}
          </div>
        </div>

        <div style={styles.filterRow}>
          <div style={{ fontWeight: "bold", fontSize: 13, color: "#666" }}>Sort By:</div>
          <div style={styles.tabGroup}>
            {[
              { id: "newest", label: "Newest" },
              { id: "best_seller", label: "Best Seller" },
              { id: "highest_orders", label: "Highest Orders" },
              { id: "low_stock", label: "Low Stock" },
              { id: "out_of_stock", label: "Out of Stock" }
            ].map(t => (
              <button 
                key={t.id} 
                onClick={() => { setSortBy(t.id); setPage(1); }}
                style={{
                  ...styles.tab,
                  ...(sortBy === t.id ? styles.tabActive : {})
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={styles.muted}>Loading stock details...</div>
        ) : filteredProducts.length === 0 ? (
          <div style={styles.empty}>No products found starting with "{search}".</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Product Details</th>
                  <th style={styles.th}>Pricing</th>
                  <th style={styles.th}>Total Uploaded</th>
                  <th style={styles.th}>Current Stock</th>
                  <th style={styles.th}>Sold Qty</th>
                  <th style={styles.th}>Pending Qty</th>
                  <th style={styles.th}>Est. Revenue</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(p => (
                  <tr key={p.id}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 800 }}>{p.name}</div>
                      <div style={styles.smallMeta}>{p.brand} • {p.category}</div>
                    </td>
                    <td style={styles.td}>
                      <b>Price:</b> {money(p.price)} <br/>
                      <span style={styles.smallMeta}>MRP: {money(p.mrp)}</span>
                    </td>
                    <td style={{...styles.td, fontWeight: 'bold'}}>{nn(p.totalUploaded)}</td>
                    <td style={{...styles.td, fontSize: 16, fontWeight: 900, color: p.currentStock === 0 ? '#b91c1c' : '#111'}}>
                      {nn(p.currentStock)}
                    </td>
                    <td style={{...styles.td, color: '#15803d', fontWeight: 800}}>{nn(p.soldQty)}</td>
                    <td style={{...styles.td, color: '#b45309', fontWeight: 800}}>{nn(p.pendingQty)}</td>
                    <td style={{...styles.td, fontWeight: 900}}>{money(p.revenue)}</td>
                    <td style={styles.td}>
                      {getStatusBadge(p)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION (Hide when searching as we fetch larger set) */}
        {!loading && !isSearching && pagination.totalPages > 1 && (
          <div style={styles.pagination}>
            <button 
              style={{...styles.btn, opacity: page === 1 ? 0.5 : 1}} 
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <div style={{ fontWeight: 'bold' }}>
              Page {page} of {pagination.totalPages} <span style={styles.smallMeta}>(Total {pagination.totalItems} items)</span>
            </div>
            <button 
              style={{...styles.btn, opacity: page >= pagination.totalPages ? 0.5 : 1}} 
              disabled={page >= pagination.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { padding: 20, minHeight: "100vh", background: "#f4f6f8", color: "#111", fontFamily: "Segoe UI, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20 },
  h1: { fontSize: 24, fontWeight: "bold", margin: 0 },
  sub: { fontSize: 14, color: "#666", marginTop: 4 },
  btn: { padding: "8px 14px", background: "#fff", border: "1px solid #ddd", borderRadius: 8, cursor: "pointer", fontWeight: "bold" },
  
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 16,
    marginBottom: 20,
  },
  summaryBox: { padding: 16, borderRadius: 12, border: "1px solid #eee" },
  summaryLabel: { fontSize: 13, fontWeight: "bold", opacity: 0.8 },
  summaryValue: { fontSize: 24, fontWeight: 900, marginTop: 6 },
  
  boxBlue: { background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" },
  boxGreen: { background: "#f0fdf4", color: "#15803d", borderColor: "#bbf7d0" },
  boxPurple: { background: "#faf5ff", color: "#7e22ce", borderColor: "#e9d5ff" },
  boxOrange: { background: "#fff7ed", color: "#c2410c", borderColor: "#fed7aa" },
  boxTeal: { background: "#f0fdfa", color: "#0f766e", borderColor: "#99f6e4" },
  boxRed: { background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" },

  card: { background: "#fff", padding: 20, borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" },
  
  filterRow: { display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 20, paddingBottom: 14, borderBottom: "1px solid #eee" },
  tabGroup: { display: "flex", gap: 8, flexWrap: "wrap" },
  tab: { 
    padding: "6px 14px", borderRadius: 20, border: "1px solid #ddd", 
    background: "#f9f9f9", cursor: "pointer", fontSize: 13, fontWeight: "bold", color: "#555" 
  },
  tabActive: { background: "#111", color: "#fff", borderColor: "#111" },

  searchRow: { marginBottom: 16 },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    background: '#f8fafc',
    border: '1.5px solid #e2e8f0',
    borderRadius: '12px',
    padding: '0 14px',
    maxWidth: '500px',
    height: '46px',
    gap: '10px',
    transition: 'border-color .2s',
  },
  searchIcon: { fontSize: 16, opacity: 0.6 },
  searchInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155'
  },
  searchClear: {
    background: '#e2e8f0',
    border: 'none',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#64748b'
  },

  table: { width: "100%", borderCollapse: "collapse", minWidth: 900 },
  th: { textAlign: "left", padding: "12px 10px", borderBottom: "2px solid #eee", fontSize: 13, color: "#666", textTransform: "uppercase" },
  td: { padding: "12px 10px", borderBottom: "1px solid #f2f2f2", fontSize: 14 },
  
  smallMeta: { fontSize: 12, color: "#888", fontWeight: "normal", marginTop: 2 },
  badge: { padding: "4px 8px", borderRadius: 6, fontSize: 11, fontWeight: "bold" },
  
  muted: { color: "#666", fontStyle: "italic", padding: 20 },
  empty: { padding: 30, textAlign: "center", fontStyle: "italic", color: "#888" },

  pagination: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, paddingTop: 14, borderTop: "1px solid #eee" },
};
